import { UNIT_LABELS } from '@bienenkampf/shared';
import { Button, Progress } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { formatAbsRel, formatDuration } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import type { HiveView } from './types.js';

export function TrainQueue({ view, now }: { view: HiveView; now: Date }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const key = trpc.hive.get.queryKey({ hiveId: view.id });
  const cancel = useMutation(
    trpc.hive.train.cancel.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: key }),
    }),
  );

  // Naechste Lieferung faellig -> nachladen (Sicherheitsnetz neben dem WebSocket-Event).
  const head = view.trainQueue[0];
  const due = head?.nextAt ? head.nextAt.getTime() <= now.getTime() : false;
  useEffect(() => {
    if (due) {
      const t = window.setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 2000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [due, qc, key]);

  if (view.trainQueue.length === 0)
    return <p className="text-sm text-stone-400">Niemand in Ausbildung.</p>;

  return (
    <ul className="flex flex-col gap-3">
      {view.trainQueue.map((q) => {
        const running = !!q.startedAt && !!q.unitSeconds;
        // Clientseitige Schaetzung der bereits fertigen Bienen; der Server bestaetigt per Event.
        const est = running
          ? Math.min(
              q.count,
              Math.floor((now.getTime() - q.startedAt!.getTime()) / 1000 / q.unitSeconds!),
            )
          : 0;
        const done = Math.max(q.delivered, est);
        const remainingSec = q.finishesAt ? (q.finishesAt.getTime() - now.getTime()) / 1000 : null;
        return (
          <li key={q.id} className="rounded-lg bg-stone-900 p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium">
                  {q.count}× {UNIT_LABELS[q.type]}
                </div>
                <div className="text-xs text-stone-400">
                  {running ? (
                    <>
                      {done}/{q.count} fertig
                      {remainingSec !== null && remainingSec > 0 && (
                        <> · Ende {formatAbsRel(q.finishesAt!, now)}</>
                      )}
                    </>
                  ) : (
                    'wartet'
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancel.mutate({ hiveId: view.id, queueId: q.id })}
                disabled={cancel.isPending}
                title="Abbrechen (offene Bienen werden erstattet)"
              >
                ✕
              </Button>
            </div>
            {running && <Progress value={done / q.count} className="mt-2" tone="sky" />}
            {running && q.nextAt && q.nextAt > now && done < q.count && (
              <div className="mt-1 text-right text-xs tabular-nums text-stone-500">
                nächste in {formatDuration((q.nextAt.getTime() - now.getTime()) / 1000)}
              </div>
            )}
          </li>
        );
      })}
      {cancel.error && <li className="text-sm text-red-400">{cancel.error.message}</li>}
    </ul>
  );
}
